import React, { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  DialogActions,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import { useProjects } from "../../hooks/useProjects";
import { useManifiestos } from "../../hooks/useManifest";
import { useAuthContext } from "../../context/AuthProvider";
import { format, parseISO, isAfter, isEqual } from "date-fns";

// ✅ Esquema de validación con Yup
const schema = yup.object().shape({
  proid: yup.number().required("Debe seleccionar un proyecto"),
  manfec: yup.date().required("Debe ingresar la fecha de salida"),
  manfecfin: yup.date()
    .nullable()
    .test("fecha-valida", "La fecha de fin debe ser posterior a la de salida", function (value) {
      const { manfec } = this.parent;
      return value ? isAfter(value, manfec) || isEqual(value, manfec) : true;
    }),
  manori: yup.string().max(255, "Máximo 255 caracteres").required("El origen es obligatorio"),
  mandes: yup.string().max(255, "Máximo 255 caracteres").required("El destino es obligatorio"),
  maninf: yup.string().max(255, "Máximo 255 caracteres"),
});

const ManifiestoForm = ({ open, handleClose, defaultValues }) => {
  const isEditing = Boolean(defaultValues?.manid);
  const { projects, isLoading: loadingProjects } = useProjects();
  const { createMutation, updateMutation } = useManifiestos();
  const { user } = useAuthContext();

  const {
    control,
    handleSubmit,
    register,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues,
  });

  // Resetear el formulario cuando cambia `defaultValues` o se cierra el modal
  useEffect(() => {
    if (open) {
      reset({
        ...defaultValues,
        manfec: defaultValues?.manfec ? format(parseISO(defaultValues.manfec), "yyyy-MM-dd") : "",
        manfecfin: defaultValues?.manfecfin ? format(parseISO(defaultValues.manfecfin), "yyyy-MM-dd") : "",
      });
    }
  }, [open, defaultValues, reset]);

  const formatDateOnly = (date) => {
    if (!date) return null;
    if (typeof date === 'string') return date; // ya está en el formato correcto
    return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')}`;
  };

  const onSubmit = (data) => {
    const formattedData = {
      ...data,
      manfec: formatDateOnly(data.manfec),
      manfecfin: formatDateOnly(data.manfecfin),
    };
  
    if (isEditing) {
      updateMutation.mutate(
        { id: defaultValues.manid, data: { ...formattedData, updbyid: user.EmpID } },
        { onSuccess: () => handleClose() }
      );
    } else {
      createMutation.mutate(
        { ...formattedData, crebyid: user.EmpID },
        { onSuccess: () => handleClose() }
      );
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>{isEditing ? `Editar Manifiesto MN-${String(defaultValues?.manid).padStart(6, "0")}` : "Crear Manifiesto"}</DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          {/* Selección de Proyecto */}
          <Controller
            name="proid"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                select
                label="Proyecto"
                fullWidth
                margin="normal"
                error={!!errors.proid}
                helperText={errors.proid?.message}
                disabled={loadingProjects}
              >
                {loadingProjects ? (
                  <MenuItem disabled>
                    <CircularProgress size={20} />
                  </MenuItem>
                ) : projects.length > 0 ? (
                  projects.map((project) => (
                    <MenuItem key={project.proid} value={project.proid}>
                      {project.pronom}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem value="" disabled>
                    No hay proyectos disponibles
                  </MenuItem>
                )}
              </TextField>
            )}
          />

          {/* Informacion */}
          <TextField
            label="Descripcion"
            fullWidth
            margin="normal"
            {...register("maninf")}
            error={!!errors.maninf}
            helperText={errors.maninf?.message}
          />

          {/* Fecha de Salida */}
          <TextField
            label="Fecha de Salida"
            type="date"
            fullWidth
            margin="normal"
            {...register("manfec")}
            error={!!errors.manfec}
            helperText={errors.manfec?.message}
            InputLabelProps={{ shrink: true }}
          />

          {/* Fecha de Fin */}
          <TextField
            label="Fecha de Fin (Opcional)"
            type="date"
            fullWidth
            margin="normal"
            {...register("manfecfin")}
            error={!!errors.manfecfin}
            helperText={errors.manfecfin?.message}
            InputLabelProps={{ shrink: true }}
          />

          {/* Origen */}
          <TextField
            label="Origen"
            fullWidth
            margin="normal"
            {...register("manori")}
            error={!!errors.manori}
            helperText={errors.manori?.message}
          />

          {/* Destino */}
          <TextField
            label="Destino"
            fullWidth
            margin="normal"
            {...register("mandes")}
            error={!!errors.mandes}
            helperText={errors.mandes?.message}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>Cancelar</Button>
          <Button type="submit" variant="contained">
            {isEditing ? "Actualizar" : "Guardar"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ManifiestoForm;
